import type { JSX } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { usePermissions } from '@/hooks/usePermissions'
import { navItemPermissions, NavItem, type NavGroupConfig, type NavItemConfig } from './NavItem'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

/** One rendered slot in the nav: either a plain link, or a dropdown of related links. */
export type NavEntry =
  | { kind: 'item'; item: NavItemConfig }
  | { kind: 'group'; group: NavGroupConfig; items: NavItemConfig[] }

/**
 * Collapse items that declare the same group into a single dropdown entry, leaving
 * ungrouped items alone. A group takes the position of its FIRST member, so nav order
 * stays driven by feature-registration order rather than by an ordering rule here.
 */
export function groupNavItems(items: NavItemConfig[]): NavEntry[] {
  const entries: NavEntry[] = []
  const groups = new Map<string, Extract<NavEntry, { kind: 'group' }>>()

  for (const item of items) {
    if (!item.group) {
      entries.push({ kind: 'item', item })
      continue
    }
    const existing = groups.get(item.group.label)
    if (existing) {
      existing.items.push(item)
      continue
    }
    const entry: Extract<NavEntry, { kind: 'group' }> = {
      kind: 'group',
      group: item.group,
      items: [item],
    }
    groups.set(item.group.label, entry)
    entries.push(entry)
  }

  return entries
}

function isPathActive(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(path + '/')
}

/**
 * A nav dropdown — e.g. "Usage" opening onto AI and Pipe.
 *
 * Permissions are filtered per CHILD, and the whole menu disappears when nothing survives.
 * Without that, a user who can't reach any of the pages would still see the trigger and
 * open an empty menu.
 */
export const NavMenu = ({ group, items }: { group: NavGroupConfig; items: NavItemConfig[] }): JSX.Element | null => {
  const location = useLocation()
  const { hasAnyPermission } = usePermissions()

  const visible = items.filter((item): boolean => {
    const required = navItemPermissions(item)
    return !required || required.length === 0 || hasAnyPermission(required)
  })

  if (visible.length === 0) return null

  const isActive = visible.some((item): boolean => isPathActive(location.pathname, item.path))
  const Icon = group.icon

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={isActive ? 'secondary' : 'ghost'}
          size="sm"
          className={cn(
            'w-full justify-start gap-2',
            isActive && 'bg-sidebar-accent text-sidebar-accent-foreground font-medium',
          )}
        >
          {Icon && <Icon className="h-4 w-4 shrink-0" />}
          <span>{group.label}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {visible.map((item): JSX.Element => {
          const ItemIcon = item.icon
          return (
            <DropdownMenuItem key={item.path} asChild>
              <Link
                to={item.path}
                className={cn(
                  'flex cursor-pointer items-center gap-2',
                  isPathActive(location.pathname, item.path) && 'font-medium',
                )}
              >
                {ItemIcon && <ItemIcon className="h-4 w-4 shrink-0" />}
                <span>{item.label}</span>
              </Link>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Render a list of nav items, collapsing grouped ones into dropdowns. */
export const NavEntries = ({ items }: { items: NavItemConfig[] }): JSX.Element => (
  <>
    {groupNavItems(items).map((entry): JSX.Element =>
      entry.kind === 'group' ? (
        <NavMenu key={`group:${entry.group.label}`} group={entry.group} items={entry.items} />
      ) : (
        <NavItem key={entry.item.path} item={entry.item} />
      ),
    )}
  </>
)
